import React, { useEffect } from 'react'
import { ReactiveVar } from 'meteor/reactive-var'
import { FormattedMessage, useIntl } from 'react-intl'
import { Meteor } from 'meteor/meteor'

import { Card, makeStyles } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'

import { LIMIT_COLOR_STAR } from '../constants/lawyer'
import { ucfirst } from '../fonction'
import useCategories from '../hooks/useCategories'
import useStateSearch from '../hooks/useStateSearch'
import getReasonsCall from '../lib/getReasonsCall'
import useTracker from '../lib/useTracker'
import { queueControl } from '../LoaderData'
import { useGlobalState } from '../store'
import type { GlobalState } from '../types/globalState'
import LawyerDomain from './LawyerDomain'
import LawyerPicture from './LawyerPicture'

const useStyle = makeStyles({
  lawyerName: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  background: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 8,
    paddingBottom: 8,
    background: '#f5f6fa',
    borderBottom: `1px solid #e2e8ee`,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  countContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  containerText: {
    paddingLeft: 16,
  },
  reasonText: {
    fontWeight: 600,
    margin: 24,
  },
  domainText: {
    fontWeight: 400,
  },
  domainContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: 24,

    '& img': {
      width: 21,
      marginRight: 16,
    },
  },
  chooseContainer: {
    color: '#FF3755',
    marginLeft: 16,
    marginRight: 16,
    marginBottom: 16,
    fontSize: 14,
  },
  phoneContainer: {
    marginLeft: 16,
    marginRight: 16,
    marginBottom: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  divider: {
    height: 1,
    width: '100%',
    backgroundColor: '#D5DEE6',
    marginBottom: 16,
  },
})

export const ShowWarningLawyer = new ReactiveVar(false)

const ShowLawyer = ({ lawyerToDisplay, step }) => {
  const classes = useStyle()
  const { formatMessage } = useIntl()
  const { categories } = useCategories()
  const { keywordSelected, reason } = useStateSearch()
  const [{ listOffers }]: GlobalState = useGlobalState('offers')

  const showWarning = useTracker(() => ShowWarningLawyer.get(), [])
  const user = useTracker(() => Meteor.user(), [])

  if (!lawyerToDisplay) return null

  const reasons = getReasonsCall(formatMessage)
  const parent = categories.find((c) => c._id === keywordSelected?.idParent)

  const offer = listOffers?.find((o) => o._id === user?.profile?.offerId)

  return (
    <Card elevation={1}>
      <div className={classes.background}>
        <div className={classes.container}>
          <div>
            <LawyerPicture zIndex={1} lawyerToDisplay={lawyerToDisplay} size={64} />
          </div>

          <div className={classes.containerText}>
            <div className={classes.lawyerName}>
              <div>
                {ucfirst(lawyerToDisplay.firstname)} {ucfirst(lawyerToDisplay.lastname)}{' '}
              </div>
              {lawyerToDisplay.reviewsCount > 0 && (
                <div style={{ display: 'inline-block', marginLeft: 8 }}>
                  <div className={classes.countContainer}>
                    <div>
                      <img
                        src={`/assets/${
                          lawyerToDisplay.ratingLawyer >= LIMIT_COLOR_STAR
                            ? 'star_gold'
                            : 'star_black'
                        }.svg`}
                        width={'14px'}
                      />
                    </div>
                    <div>{Number(lawyerToDisplay.ratingLawyer || 0).toFixed(1)}</div>
                  </div>
                </div>
              )}
            </div>

            <LawyerDomain lawyerToDisplay={lawyerToDisplay} />
          </div>
        </div>
      </div>

      <div>
        <div className={classes.reasonText}>{reasons[reason]}</div>

        {keywordSelected && (
          <div className={classes.domainContainer}>
            <img
              src={`/assets/category/${
                keywordSelected?.imageUrlWithExtension || parent?.imageUrlWithExtension
              }`}
              alt={keywordSelected?.name}
            />
            <div className={classes.domainText}>{keywordSelected?.name}</div>
          </div>
        )}
      </div>

      {showWarning && (
        <div className={classes.chooseContainer}>
          <FormattedMessage
            id={'ShowLawyer.ChooseEntreprise'}
            defaultMessage={
              'Vous devez choisir un compte entreprise pour appeler un avocat dans ce domaine.'
            }
          />
        </div>
      )}

      {step >= 3 && (
        <>
          <div className={classes.divider} />

          <div className={classes.phoneContainer}>
            <b>
              <FormattedMessage
                id={'ShowLawyer.KeepYourPhone'}
                defaultMessage={
                  'Gardez votre téléphone près de vous, nous allons vous mettre en relation avec ce numéro'
                }
              />
            </b>
            <div style={{ marginLeft: 16, marginTop: 24 }}>
              <img src={'/assets/profil/tel.svg'} width={16} style={{ marginRight: 16 }} />

              {user?.profile.phone}
            </div>
          </div>
        </>
      )}

      {step >= 4 && offer && (
        <>
          <div className={classes.divider} />

          <div className={classes.phoneContainer}>
            <b>
              <FormattedMessage
                id={'ShowLawyer.YourOffer'}
                defaultMessage={"Vous bénéficiez de l'offre :"}
              />
            </b>

            <div className={classes.container} style={{ marginTop: 24 }}>
              <CheckIcon style={{ color: '#43BC23', marginLeft: 16, marginRight: 16 }} />
              <div>{offer.title}</div>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}

export default React.memo((props) => {
  const [{ profileLawyer }] = useGlobalState('lawyers')
  const { lawyerId } = useStateSearch()

  const lawyerToDisplay = profileLawyer[lawyerId]

  useEffect(() => {
    queueControl.push({ _id: lawyerId })
  }, [])

  return <ShowLawyer lawyerToDisplay={lawyerToDisplay} {...props} />
})
